// ErrorReporter — a tiny runtime-agnostic seam for surfacing per-item failures and
// anomalies to Sentry WITHOUT shared/ importing a concrete Sentry SDK.
//
// shared/services runs in three runtimes (Cloud Run / Node, Deno edge functions, and
// Vitest). Each has its own Sentry entry point (@sentry/node in the pipeline, the edge
// _shared/sentry.ts in Deno). Instead of importing one of them (which would break the
// others), shared services accept an optional ErrorReporter and default to a
// console-only implementation. Entry points inject a Sentry-backed reporter.
//
// This is the canonical way for a shared service to say "this is worth a Sentry event"
// while staying pure/portable and unit-testable.

export interface ErrorReporter {
  /** Report an exception (a failed operation with an Error object). */
  captureException(error: unknown, context?: Record<string, unknown>): void;
  /** Report a noteworthy message (an anomaly / summary that isn't itself a thrown error). */
  captureMessage(message: string, context?: Record<string, unknown>): void;
}

/**
 * Default reporter: logs to console only. Used in tests and any runtime that does not
 * inject a Sentry-backed reporter. Never throws — reporting must not break the caller.
 */
export const consoleErrorReporter: ErrorReporter = {
  captureException(error: unknown, context?: Record<string, unknown>): void {
    console.error('[reporter] exception', error, context ?? '');
  },
  captureMessage(message: string, context?: Record<string, unknown>): void {
    console.warn('[reporter] message', message, context ?? '');
  },
};
